import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { env } from '@/config/env';
import type { Database } from '@/types/database.types';

/**
 * Supabase Client Configuration
 *
 * CRITICAL SECURITY NOTICE:
 * This client runs strictly in the browser using the public anonymous key (`anon`).
 * Service-role keys or privileged database passwords must NEVER be used here.
 */

// Fallback dummy values to prevent crash when env variables are not yet populated during development
const FALLBACK_URL = 'https://placeholder.supabase.co';
const FALLBACK_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.e30.placeholder';

const resolvedUrl =
  env.supabaseUrl && env.supabaseUrl.startsWith('http') ? env.supabaseUrl : FALLBACK_URL;

const resolvedKey =
  env.supabaseAnonKey && env.supabaseAnonKey.length > 10
    ? env.supabaseAnonKey
    : FALLBACK_KEY;

export const supabase: SupabaseClient<Database> = createClient<Database>(
  resolvedUrl,
  resolvedKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  }
);

export interface SupabaseHealthResult {
  isConfigured: boolean;
  isConnected: boolean;
  statusMessage: string;
  url: string;
  latencyMs?: number;
}

/**
 * Safely performs a non-destructive ping/health check against Supabase.
 */
export async function checkSupabaseConnection(): Promise<SupabaseHealthResult> {
  if (!env.isSupabaseConfigured) {
    return {
      isConfigured: false,
      isConnected: false,
      statusMessage:
        'Supabase credentials are not configured or are set to placeholder values in .env',
      url: env.supabaseUrl || 'None',
    };
  }

  const startTime = performance.now();
  try {
    // Attempt a light read on public templates table or auth status
    const { error } = await supabase.from('templates').select('id').limit(1);
    const latencyMs = Math.round(performance.now() - startTime);

    if (
      error &&
      error.code !== 'PGRST116' &&
      !error.message.includes('permission denied')
    ) {
      // If table doesn't exist yet (Phase 2), but Supabase responded with a valid PostgREST code
      if (error.code === '42P01') {
        return {
          isConfigured: true,
          isConnected: true,
          statusMessage:
            'Connected to Supabase PostgreSQL (MVP schema pending Phase 2 migrations)',
          url: env.supabaseUrl,
          latencyMs,
        };
      }
      return {
        isConfigured: true,
        isConnected: false,
        statusMessage: `Supabase returned error: ${error.message}`,
        url: env.supabaseUrl,
        latencyMs,
      };
    }

    return {
      isConfigured: true,
      isConnected: true,
      statusMessage: 'Connected successfully to Supabase',
      url: env.supabaseUrl,
      latencyMs,
    };
  } catch (err) {
    const latencyMs = Math.round(performance.now() - startTime);
    const message = err instanceof Error ? err.message : 'Unknown connection error';
    return {
      isConfigured: true,
      isConnected: false,
      statusMessage: `Network/Connection error: ${message}`,
      url: env.supabaseUrl,
      latencyMs,
    };
  }
}
